import { Phone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useVoiceStore } from '../../state/voice-ui.store';
import { DialerNumberField } from './dialer-number-field';
import { DialKeypad } from './dial-keypad';

/** Mirrors the backend's `InitiateCallDto` E.164 rule — the Call button
 *  stays disabled until the number would be accepted server-side. */
const E164_RE = /^\+[1-9]\d{6,14}$/;

const DEFAULT_COUNTRY = 'us';
/**
 * Dial code matching DEFAULT_COUNTRY above. `PhoneInput` composes typed
 * digits with its visible country selector, but keypad presses land here
 * before the input has ever emitted a value — on that pristine state we
 * must prepend the dial code the selector is showing. Any later country
 * change flows through `onChange`, so this seed never overrides it.
 */
const DEFAULT_DIAL_CODE = '+1';

interface DialerPanelProps {
    readonly onDial: (toNumber: string) => void;
    readonly isDialing: boolean;
    /** When set, outbound dialing is blocked (agent Offline, or plan voice
     *  minutes exhausted) — the Call button is disabled and the reason is
     *  rendered above it. */
    readonly disabledReason?: string | null;
}

/**
 * Idle-state manual dialer — country-aware number entry + keypad grid.
 * The country selector owns the prefix; there is no "bare 10 digits must
 * be US" guessing anymore.
 */
export function DialerPanel({ onDial, isDialing, disabledReason }: DialerPanelProps) {
    // Minimizing unmounts the widget subtree, so the typed number lives in the
    // store — local state was being thrown away every time the user collapsed
    // the dialer or switched to the History/Contacts tab.
    const number = useVoiceStore((s) => s.dialerDraft);
    const setNumber = useVoiceStore((s) => s.setDialerDraft);
    const canDial = E164_RE.test(number) && !disabledReason;

    const appendDigit = (digit: string): void => {
        // '*'/'#' are DTMF-only (in-call keypad) — not dialable digits, and
        // the backend rejects them in `toNumber`.
        if (digit === '*' || digit === '#') return;
        setNumber(number === '' ? `${DEFAULT_DIAL_CODE}${digit}` : `${number}${digit}`);
    };

    /**
     * Hold-zero gesture. '+' is only ever the E.164 prefix, so a second one
     * could never produce a dialable number — on a value that already carries
     * its prefix (every non-empty one, since `appendDigit` seeds the dial code)
     * the hold is a no-op. On an empty field it starts a number whose country
     * code the user types by hand.
     */
    const insertPlusPrefix = (): void => {
        if (number.includes('+')) return;
        setNumber(`+${number}`);
    };

    const handleSubmit = (event: React.FormEvent): void => {
        event.preventDefault();
        if (!canDial || isDialing) return;
        onDial(number);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col gap-2 p-[calc(0.75rem*var(--voice-scale,1))]"
        >
            <DialerNumberField
                value={number}
                onChange={setNumber}
                defaultCountry={DEFAULT_COUNTRY}
            />
            <DialKeypad onPress={appendDigit} onHoldZero={insertPlusPrefix} />
            {/* Rendered, not just a `title` — a native tooltip on a *disabled*
                button never fires, so the reason was invisible to the agent
                staring at a greyed-out Call button. */}
            {disabledReason && (
                <p
                    role="status"
                    className="mt-1 text-center text-[length:calc(0.75rem*var(--voice-scale,1))] leading-snug text-text-tertiary"
                >
                    {disabledReason}
                </p>
            )}
            <Button
                type="submit"
                title={disabledReason ?? undefined}
                className="mt-1 h-[calc(2.375rem*var(--voice-scale,1))] w-full gap-2 rounded-full bg-success-600 text-[length:calc(0.875rem*var(--voice-scale,1))] text-white shadow-[0_8px_20px_-6px_rgb(34_197_94/0.45)] transition-all hover:bg-success-700 active:bg-success-800 motion-safe:active:scale-[0.98] dark:shadow-[0_8px_20px_-6px_rgb(34_197_94/0.35)]"
                disabled={!canDial || isDialing}
            >
                <Phone className="size-[calc(1rem*var(--voice-scale,1))]" />
                {isDialing ? 'Calling…' : 'Call'}
            </Button>
        </form>
    );
}
